import { useState, type ReactNode } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { Sheet } from './Sheet';
import { Button } from './Button';
import { MoneyInput } from './MoneyInput';
import { controlClass } from './FormSection';

export interface RowOption {
  value: string;
  label: string;
}

interface CommonProps {
  label: string;
  /** Validation message; renders as a red line under the label. */
  error?: string;
}

type RowFieldProps =
  | (CommonProps & { type: 'text' | 'multiline'; value: string; placeholder?: string; onCommit: (v: string) => void })
  | (CommonProps & { type: 'currency'; value: number; placeholder?: string; onCommit: (v: number) => void })
  | (CommonProps & { type: 'date'; value: string; onCommit: (v: string) => void })
  | (CommonProps & { type: 'select'; value: string; options: RowOption[]; placeholder?: string; onCommit: (v: string) => void })
  | (CommonProps & { type: 'toggle'; value: boolean; onChange: (v: boolean) => void });

/**
 * Pack B2 core interaction. A tappable row (bold label left, value right, chevron)
 * that opens a focused editor for JUST this field in a {@link Sheet}. The `toggle`
 * variant is inline (a switch, no drill-in). Validation errors render as a red
 * line under the label, not a toast.
 */
export function RowField(props: RowFieldProps) {
  const [open, setOpen] = useState(false);

  if (props.type === 'toggle') {
    return (
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <Label label={props.label} error={props.error} />
        <Switch checked={props.value} onChange={props.onChange} label={props.label} />
      </div>
    );
  }

  const display = displayValue(props);
  const isEmpty = display == null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-slate-50"
      >
        <Label label={props.label} error={props.error} />
        <span className="flex items-center gap-1.5 text-sm">
          <span className={`${isEmpty ? 'text-slate-400' : 'text-ink'} ${props.type === 'currency' ? 'font-mono' : ''} truncate max-w-[9rem] sm:max-w-[14rem]`}>
            {isEmpty ? placeholderOf(props) : display}
          </span>
          <ChevronRight size={16} className="shrink-0 text-slate-400" />
        </span>
      </button>
      {open && <Editor props={props} onClose={() => setOpen(false)} />}
    </>
  );
}

function Label({ label, error }: { label: string; error?: string }) {
  return (
    <span className="min-w-0">
      <span className="block text-sm font-medium text-ink">{label}</span>
      {error && <span className="block text-xs text-danger mt-0.5">{error}</span>}
    </span>
  );
}

/** A group of RowFields on one white card with hairline separators + optional title. */
export function RowGroup({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="space-y-1.5">
      {title && <h2 className="px-1 font-display text-sm font-semibold text-slate-500">{title}</h2>}
      <div className="overflow-hidden rounded-2xl bg-surface shadow-sm ring-1 ring-slate-200/70 divide-y divide-slate-100">
        {children}
      </div>
    </section>
  );
}

// ── internals ────────────────────────────────────────────────────────────────

function displayValue(props: Exclude<RowFieldProps, { type: 'toggle' }>): string | null {
  switch (props.type) {
    case 'currency':
      return `$${props.value.toFixed(2)}`;
    case 'select':
      return props.options.find((o) => o.value === props.value)?.label ?? null;
    case 'date':
      return props.value ? props.value : null;
    default:
      return props.value ? props.value : null;
  }
}

function placeholderOf(props: Exclude<RowFieldProps, { type: 'toggle' }>): string {
  if ('placeholder' in props && props.placeholder) return props.placeholder;
  return 'Tap to set';
}

function Editor({ props, onClose }: { props: Exclude<RowFieldProps, { type: 'toggle' }>; onClose: () => void }) {
  // select commits immediately on tap; the rest use a draft + Save.
  if (props.type === 'select') {
    return (
      <Sheet open onClose={onClose} title={props.label}>
        <ul className="space-y-1">
          {props.options.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                onClick={() => {
                  props.onCommit(o.value);
                  onClose();
                }}
                className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left text-sm text-ink hover:bg-slate-50"
              >
                {o.label}
                {o.value === props.value && <Check size={16} className="text-pine" />}
              </button>
            </li>
          ))}
        </ul>
      </Sheet>
    );
  }
  return <DraftEditor props={props} onClose={onClose} />;
}

function DraftEditor({
  props,
  onClose
}: {
  props: Exclude<RowFieldProps, { type: 'toggle' | 'select' }>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(props.value);

  const commit = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props.onCommit as (v: any) => void)(draft);
    onClose();
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={props.label}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={commit}>Save</Button>
        </div>
      }
    >
      {props.type === 'currency' && (
        <MoneyInput value={draft as number} onChange={(n) => setDraft(n)} ariaLabel={props.label} />
      )}
      {props.type === 'multiline' && (
        <textarea
          autoFocus
          className={`${controlClass} min-h-[120px]`}
          value={draft as string}
          onChange={(e) => setDraft(e.target.value)}
        />
      )}
      {props.type === 'date' && (
        <input
          autoFocus
          type="date"
          className={controlClass}
          value={draft as string}
          onChange={(e) => setDraft(e.target.value)}
        />
      )}
      {props.type === 'text' && (
        <input
          autoFocus
          className={controlClass}
          value={draft as string}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
          }}
        />
      )}
    </Sheet>
  );
}

function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pine focus:ring-offset-1 ${
        checked ? 'bg-pine' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}
